// SizeChangeModal - マップサイズ変更モーダル
// プリセット（4/5/7/10）からマップサイズを選び直す。縮小時は範囲外の配置を削除。
// トップ画面と同じ専用ダイアログ（InScreenModal + BuilderModalPanel）で表示する。

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { Vector3D } from "common/types";
import { usePlaySound, btnDelay } from "common/utils";
import { recordEditHistory } from "features/builder/hook";
import { useBuilderContext } from "features/builder/context/BuilderContext";
import { actions, selectAllMapModel, countOutOfBounds } from "features/builder/slice";
import {
  PillSize,
  PillColor,
  InScreenModal,
  BuilderModalPanel,
  BuilderPillButton,
  BuilderModalAccent,
} from "features/builder/elements";

const SIZE_PRESETS = [4, 5, 7, 10];

export const SizeChangeModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const { triggerReload } = useBuilderContext();

  const size = useSelector((state: RootState) => state.builder.size);
  const mapModels = useSelector(selectAllMapModel);

  const [selected, setSelected] = useState(size.x);

  // 開くたびに現在サイズで初期化
  useEffect(() => {
    if (open) setSelected(size.x);
  }, [open, size.x]);

  const nextSize: Vector3D = { x: selected, y: selected, z: selected };
  const removedCount = countOutOfBounds(mapModels, nextSize);

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const handleSelect = (preset: number) => {
    play();
    setSelected(preset);
  };

  const handleApply = () => {
    play();
    btnDelay(() => {
      dispatch(actions.setMapSize(nextSize));
      dispatch(recordEditHistory("resize_map"));
      triggerReload();
      onClose();
    });
  };

  return (
    <InScreenModal open={open} onBackdropClose={handleClose}>
      <BuilderModalPanel
        accent={BuilderModalAccent.BLUE}
        icon="grid_view"
        title={I18n.t("MSG_BUILDER_SIZE_MODAL_TITLE")}
        width={480}
        onClose={handleClose}
      >
        {/* プリセット */}
        <div className="flex-row-el w-full gap-3">
          {SIZE_PRESETS.map((preset) => {
            const isSelected = preset === selected;
            return (
              <button
                key={preset}
                type="button"
                onClick={() => handleSelect(preset)}
                className={`type-inherit-font flex-col-el flex-center flex-1 gap-1 py-4 rounded-xl border-solid border-2 cursor-pointer transition-all active:scale-95 ${
                  isSelected ? "bg-blue-10 border-blue-100" : "bg-white border-gray2-60"
                }`}
              >
                <span
                  className={`type-text type-title ${
                    isSelected ? "text-blue-100" : "text-textcolor-black/50"
                  }`}
                >
                  {preset}×{preset}
                </span>
              </button>
            );
          })}
        </div>

        {/* 縮小警告 */}
        {removedCount > 0 && (
          <div className="flex-row-el items-center w-full gap-2 p-4 rounded-xl bg-orange-100/20 border-solid border-2 border-orange-100">
            <span className="material-symbols-outlined type-md text-orange-100">warning</span>
            <span className="type-text-left type-sm text-textcolor-black flex-1">
              {I18n.t("MSG_BUILDER_SIZE_SHRINK_WARNING", { count: removedCount })}
            </span>
          </div>
        )}

        {/* フッター */}
        <div className="flex-row-el flex-center w-full gap-4 pb-2">
          <BuilderPillButton
            color={PillColor.BLUE}
            size={PillSize.MD}
            icon="close"
            label={I18n.t("MSG_CLOSE_BTN")}
            onClick={handleClose}
          />
          <BuilderPillButton
            color={PillColor.GREEN}
            size={PillSize.MD}
            icon="check"
            label={I18n.t("MSG_CONFIRM_BTN")}
            onClick={handleApply}
          />
        </div>
      </BuilderModalPanel>
    </InScreenModal>
  );
};
